import UserAgentService from '@/utils/user-agent-service';
import ExtensionCommunicationService from '@/utils/extension-communication-service';
import { FeatureType } from './Steps/ChooseProjectAndFeatureType/FeatureTypeSelector/feature-type-selector.const';
import AppService from '@/utils/AppService';

/**
 * Steps Components
 */
import CreateAutomation from './Steps/CreateAutomation/CreateAutomation.vue';
import GoToExtToInstallAddOn from './Steps/GoToExtToInstallAddOn/GoToExtToInstallAddOn.vue';
import ChooseProjectAndFeatureType from './Steps/ChooseProjectAndFeatureType/ChooseProjectAndFeatureType.vue';
import ChooseAppForAddOnFeature from './Steps/ChooseAppForAddOnFeature/ChooseAppForAddOnFeature.vue';
import ChooseAppForButtonDropdown from './Steps/ChooseAppForButtonDropdown/ChooseAppForButtonDropdown.vue';
import ChooseInstallableForAddOn from './Steps/ChooseInstallableForAddOn/ChooseInstallableForAddOn.vue';
import ChooseCredentialsDataProvider from './Steps/ChooseCredentials/ChooseCredentialsDataProvider.vue';
import ChoosePlacementForAddOnDataProvider from './Steps/ChoosePlacementForAddOn/ChoosePlacementForAddOnDataProvider.vue';
import GoToAddOnApp from './Steps/GoToAddOnApp/GoToAddOnApp.vue';
import NeedChromeToUseAddOn from './Steps/NeedChromeToUseAddOn/NeedChromeToUseAddOn.vue';
import NeedPlyExtensionToUseAddOn from './Steps/NeedPlyExtensionToUseAddOn/NeedPlyExtensionToUseAddOn.vue';
import ChooseInstallableForTool from './Steps/ChooseInstallableForTool/ChooseInstallableForTool.vue';
import FillToolData from './Steps/FillToolData/FillToolData.vue';
import ChooseToolPlacement from './Steps/ChooseToolPlacement/ChooseToolPlacement.vue';
import CreatePortal from './Steps/CreatePortal/CreatePortal.vue';
import CreateInternalNotification from './Steps/CreateInternalNotification/CreateInternalNotification.vue';
import FillButtonDropdownData from './Steps/FillButtonDropdownData/FillButtonDropdownData.vue';
import ChoosePlacementForButtonDropdownDataProvider from './Steps/ChoosePlacementForButtonDropdown/ChoosePlacementForButtonDropdownDataProvider.vue';
import NeedPlyExtensionToUseButtonDropdown from './Steps/NeedPlyExtensionToUseButtonDropdown/NeedPlyExtensionToUseButtonDropdown.vue';
import SlackNativeSettings from './Steps/SlackNativeFeatures/SlackNativeSettings/SlackNativeSettings.vue';
import CreateSlackFeature from './Steps/SlackNativeFeatures/CreateSlackFeature/CreateSlackFeature.vue';
import ChoosePlacementForSlackAddOn from './Steps/SlackNativeFeatures/ChoosePlacementForSlackAddon/ChoosePlacementForSlackAddon.vue';
const SLACK_SLUG = 'slack';

const CHOOSE_PROJECT_AND_FEATURE = 1;
const CHOOSE_APP_FOR_ADD_ON_FEATURE = 2;
const CHOOSE_INSTALLABLE_FOR_ADD_ON = 3;
const CHOOSE_CREDENTIALS_FOR_ADD_ON = 4;
const CHOOSE_PLACEMENT_FOR_ADD_ON = 5;
const GO_TO_ADD_ON_APP = 6;
const NEED_CHROME_TO_USE_ADD_ON = 7;
const NEED_PLY_EXTENSION_TO_USE_ADD_ON = 8;
const CHOOSE_TOOL_PLACEMENT = 9;
const CREATE_AUTOMATION = 10;
const GO_TO_EXT_TO_INSTALL_ADD_ON = 11;
const CHOOSE_INSTALLABLE_FOR_TOOL = 12;
const FILL_TOOL_DATA = 13;
const CREATE_PORTAL = 14;
const CREATE_INTERNAL_NOTIFICATION = 15;

/** Button Dropdown Creation */
const CHOOSE_APP_FOR_BUTTON_DROPDOWN = 16;
const CHOOSE_PLACEMENT_FOR_BUTTON_DROPDOWN = 17;
const FILL_BUTTON_DROPDOWN_DATA = 18;
const NEED_PLY_EXTENSION_TO_USE_BUTTON_DROPDOWN = 19;

const SLACK_SETTINGS = 20;
const CHOOSE_PLACEMENT_FOR_SLACK_ADD_ON = 21;
const SLACK_CREATE_FEATURE = 22;

export const CREATION_WIZARD_STEPS = {
	CHOOSE_PROJECT_AND_FEATURE,
	CHOOSE_APP_FOR_ADD_ON_FEATURE,
	CHOOSE_INSTALLABLE_FOR_ADD_ON,
	CHOOSE_CREDENTIALS_FOR_ADD_ON,
	CHOOSE_PLACEMENT_FOR_ADD_ON,
	GO_TO_ADD_ON_APP,
	NEED_CHROME_TO_USE_ADD_ON,
	NEED_PLY_EXTENSION_TO_USE_ADD_ON,
	CHOOSE_TOOL_PLACEMENT,
	CREATE_AUTOMATION,
	GO_TO_EXT_TO_INSTALL_ADD_ON,
	CHOOSE_INSTALLABLE_FOR_TOOL,
	FILL_TOOL_DATA,
	CREATE_PORTAL,
	CREATE_INTERNAL_NOTIFICATION,
	/** Button Dropdown */
	CHOOSE_APP_FOR_BUTTON_DROPDOWN,
	CHOOSE_PLACEMENT_FOR_BUTTON_DROPDOWN,
	FILL_BUTTON_DROPDOWN_DATA,
	NEED_PLY_EXTENSION_TO_USE_BUTTON_DROPDOWN,
	/** Slack Settings */
	SLACK_SETTINGS,
	CHOOSE_PLACEMENT_FOR_SLACK_ADD_ON,
	SLACK_CREATE_FEATURE,
};

export const FEATURE_TYPE_TO_STEP = {
	[FeatureType.AddOn]: CHOOSE_APP_FOR_ADD_ON_FEATURE,
	[FeatureType.Automation]: CREATE_AUTOMATION,
	[FeatureType.Tool]: CHOOSE_TOOL_PLACEMENT,
	[FeatureType.Portal]: CREATE_PORTAL,
	[FeatureType.InternalNotification]: CREATE_INTERNAL_NOTIFICATION,
};

const buildNextStepResponse = (nextStepComponent, keepLastStepInHistory = true) => ({
	destination: nextStepComponent,
	keepLastStepInHistory,
});

// used by CreationWizardService - to determine first step when coming from ext
export const ChooseInstallableForAppGetNextStep = ({ selectedApp }) => {
	if (selectedApp.service_slug === SLACK_SLUG) {
		// Hack to return a slack setup. This is a temporary solution until we have a proper way to handle this.
		return SLACK_SETTINGS;
	}
	return AppService.doesRequiresAuth(selectedApp) ? CHOOSE_CREDENTIALS_FOR_ADD_ON : CHOOSE_PLACEMENT_FOR_ADD_ON;
};

// TODO - remove this after enabling installables mechanism
const ChooseInstallableForAppGetNextStepResponse = ({ selectedApp }) =>
	buildNextStepResponse(ChooseInstallableForAppGetNextStep({ selectedApp }));
const ChooseInstallableForAppGetNextStepArgs = ['selectedApp'];

export const CREATION_WIZARD_STEP_CONFIG = {
	[CHOOSE_PROJECT_AND_FEATURE]: {
		component: ChooseProjectAndFeatureType,
		getNextStep: async ({ selectedProjectId, selectedFeatureTypeId }) => {
			let nextStep = buildNextStepResponse(null);
			if (selectedProjectId) {
				const needToGoToExt = FeatureType.AddOn === selectedFeatureTypeId && !(await canShowNewCreationWizard());
				nextStep = buildNextStepResponse(needToGoToExt ? GO_TO_EXT_TO_INSTALL_ADD_ON : FEATURE_TYPE_TO_STEP[selectedFeatureTypeId]);
			}
			return nextStep;
		},
		getNextStepArgs: ['selectedProjectId', 'selectedFeatureTypeId'],
	},
	[CHOOSE_APP_FOR_ADD_ON_FEATURE]: {
		component: ChooseAppForAddOnFeature,
		getNextStep: ChooseInstallableForAppGetNextStepResponse, // buildNextStepResponse(CHOOSE_INSTALLABLE_FOR_ADD_ON), // TODO - remove comment after enabling installables mechanism
		getNextStepArgs: ChooseInstallableForAppGetNextStepArgs, // [] // TODO - remove comment after enabling installables mechanism
	},
	[CHOOSE_INSTALLABLE_FOR_ADD_ON]: {
		component: ChooseInstallableForAddOn,
		getNextStep: ChooseInstallableForAppGetNextStepResponse,
		getNextStepArgs: ChooseInstallableForAppGetNextStepArgs,
	},
	[CHOOSE_CREDENTIALS_FOR_ADD_ON]: {
		component: ChooseCredentialsDataProvider,
		getNextStep: () => buildNextStepResponse(CHOOSE_PLACEMENT_FOR_ADD_ON),
		getNextStepArgs: [],
	},
	[CHOOSE_PLACEMENT_FOR_ADD_ON]: {
		component: ChoosePlacementForAddOnDataProvider,
		getNextStep: async ({ embeddedMode }) => {
			let nextStep = buildNextStepResponse(GO_TO_ADD_ON_APP);
			if (!embeddedMode) {
				const isUsingChromiumBrowser = UserAgentService.isUsingChromium();
				if (!isUsingChromiumBrowser) {
					nextStep = buildNextStepResponse(NEED_CHROME_TO_USE_ADD_ON);
				} else if (!(await ExtensionCommunicationService.isInstalled())) {
					nextStep = buildNextStepResponse(NEED_PLY_EXTENSION_TO_USE_ADD_ON);
				}
			}
			return nextStep;
		},
		getNextStepArgs: ['embeddedMode'],
	},
	[GO_TO_ADD_ON_APP]: { component: GoToAddOnApp, getNextStep: () => buildNextStepResponse(null), getNextStepArgs: [] },
	[NEED_CHROME_TO_USE_ADD_ON]: { component: NeedChromeToUseAddOn, getNextStep: () => buildNextStepResponse(null), getNextStepArgs: [] },
	[NEED_PLY_EXTENSION_TO_USE_ADD_ON]: {
		component: NeedPlyExtensionToUseAddOn,
		getNextStep: () => buildNextStepResponse(GO_TO_ADD_ON_APP, false),
		getNextStepArgs: [],
	},
	[CHOOSE_TOOL_PLACEMENT]: {
		component: ChooseToolPlacement,
		getNextStep: () => buildNextStepResponse(FILL_TOOL_DATA), // buildNextStepResponse(CHOOSE_INSTALLABLE_FOR_TOOL), // TODO - remove comment after enabling installables mechanism
		getNextStepArgs: [],
	},
	[CREATE_AUTOMATION]: { component: CreateAutomation, getNextStep: () => buildNextStepResponse(null), getNextStepArgs: [] },
	[GO_TO_EXT_TO_INSTALL_ADD_ON]: { component: GoToExtToInstallAddOn, getNextStep: () => buildNextStepResponse(null), getNextStepArgs: [] },
	[CHOOSE_INSTALLABLE_FOR_TOOL]: {
		component: ChooseInstallableForTool,
		getNextStep: () => buildNextStepResponse(FILL_TOOL_DATA),
		getNextStepArgs: [],
	},
	[FILL_TOOL_DATA]: {
		component: FillToolData,
		getNextStep: () => buildNextStepResponse(null),
		getNextStepArgs: [],
	},
	[CREATE_PORTAL]: {
		component: CreatePortal,
		getNextStep: () => buildNextStepResponse(null),
		getNextStepArgs: [],
	},
	[CREATE_INTERNAL_NOTIFICATION]: {
		component: CreateInternalNotification,
		getNextStep: () => buildNextStepResponse(null),
		getNextStepArgs: [],
	},
	/**ButtonDropdown */
	[CHOOSE_APP_FOR_BUTTON_DROPDOWN]: {
		component: ChooseAppForButtonDropdown,
		getNextStep: () => buildNextStepResponse(CHOOSE_PLACEMENT_FOR_BUTTON_DROPDOWN),
		getNextStepArgs: [],
	},
	[CHOOSE_PLACEMENT_FOR_BUTTON_DROPDOWN]: {
		component: ChoosePlacementForButtonDropdownDataProvider,
		getNextStep: async () => {
			const isUsingChromiumBrowser = UserAgentService.isUsingChromium();
			if (!isUsingChromiumBrowser) {
				return buildNextStepResponse(NEED_CHROME_TO_USE_ADD_ON);
			}
			const isSatisfyingVersionInstalled =
				(await ExtensionCommunicationService.isInstalled()) && (await ExtensionCommunicationService.doesSupportsNewPlacementRelations());
			if (!isSatisfyingVersionInstalled) {
				return buildNextStepResponse(NEED_PLY_EXTENSION_TO_USE_BUTTON_DROPDOWN);
			}
			return buildNextStepResponse(FILL_BUTTON_DROPDOWN_DATA);
		},
		getNextStepArgs: [],
	},
	[FILL_BUTTON_DROPDOWN_DATA]: {
		component: FillButtonDropdownData,
		getNextStep: () => buildNextStepResponse(null),
		getNextStepArgs: [],
	},
	[NEED_PLY_EXTENSION_TO_USE_BUTTON_DROPDOWN]: {
		component: NeedPlyExtensionToUseButtonDropdown,
		getNextStep: () => buildNextStepResponse(FILL_BUTTON_DROPDOWN_DATA, false),
		getNextStepArgs: [],
	},
	[SLACK_SETTINGS]: {
		component: SlackNativeSettings,
		getNextStep: () => buildNextStepResponse(CHOOSE_PLACEMENT_FOR_SLACK_ADD_ON),
		getNextStepArgs: [],
	},
	[CHOOSE_PLACEMENT_FOR_SLACK_ADD_ON]: {
		component: ChoosePlacementForSlackAddOn,
		getNextStep: () => buildNextStepResponse(SLACK_CREATE_FEATURE),
		getNextStepArgs: [],
	},
	[SLACK_CREATE_FEATURE]: {
		component: CreateSlackFeature,
		getNextStep: () => buildNextStepResponse(null),
		getNextStepArgs: [],
	},
};

/**
 * START
 * TODO - remove this area after all users switched to new extension and support new creation wizard officially
 */
const NEW_CREATION_WIZARD_EXTENSION_MINIMAL_VERSION = '0.1.0';
const canShowNewCreationWizard = async () =>
	!(await ExtensionCommunicationService.isInstalled()) ||
	(await ExtensionCommunicationService.isExtensionVersionGTEToVersion(NEW_CREATION_WIZARD_EXTENSION_MINIMAL_VERSION));
/**
 * END
 */

export const CREATION_MODES = Object.freeze({
	WORKFLOW: 'workflow',
	BUTTON_DROPDOWN: 'buttonDropdown',
});
